import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import ContactMeetingsAPI from 'dashboard/api/contactMeetings';

// The only place that decides "which providers can schedule a meeting" —
// driven entirely by integration manifest data (category + supports_
// scheduling), never by a provider name. FollowUp.vue renders whatever
// this returns; it never branches on a specific provider id itself.
export function useMeetingScheduler() {
  const store = useStore();
  const { t } = useI18n();
  const isScheduling = ref(false);

  const appIntegrations = useMapGetter('integrations/getAppIntegrations');
  const getIntegration = useMapGetter('integrations/getIntegration');

  // Fetched once per session — cheap generic integrations list, already
  // used by the Settings > Integrations > Calendar page. Avoids refetching
  // every time the Follow-up dialog is opened.
  if (!appIntegrations.value?.length) {
    store.dispatch('integrations/get');
  }

  const schedulingProviders = computed(() =>
    (appIntegrations.value || [])
      .filter(
        integration =>
          integration.category === 'calendar' && integration.supports_scheduling
      )
      .map(integration => ({
        id: integration.id,
        label: integration.name,
      }))
  );

  const isProviderConnected = providerId =>
    !!getIntegration.value(providerId)?.enabled;

  const providerAction = providerId => getIntegration.value(providerId)?.action;

  const applyContact = contact => {
    if (contact) store.dispatch('contacts/setContact', contact);
  };

  const scheduleMeeting = async (
    contactId,
    { provider, startAt, assignedAgentId }
  ) => {
    isScheduling.value = true;
    try {
      const response = await ContactMeetingsAPI.schedule(contactId, {
        provider,
        startAt,
        assignedAgentId,
      });
      applyContact(response.data.payload);
      useAlert(t('FOLLOW_UP.MEETING.SCHEDULE_SUCCESS'));
      return true;
    } catch (error) {
      useAlert(
        error?.response?.data?.error || t('FOLLOW_UP.MEETING.SCHEDULE_ERROR')
      );
      return false;
    } finally {
      isScheduling.value = false;
    }
  };

  const rescheduleMeeting = async (contactId, { startAt }) => {
    isScheduling.value = true;
    try {
      const response = await ContactMeetingsAPI.reschedule(contactId, {
        startAt,
      });
      applyContact(response.data.payload);
      useAlert(t('FOLLOW_UP.MEETING.RESCHEDULE_SUCCESS'));
      return true;
    } catch (error) {
      useAlert(
        error?.response?.data?.error || t('FOLLOW_UP.MEETING.RESCHEDULE_ERROR')
      );
      return false;
    } finally {
      isScheduling.value = false;
    }
  };

  const cancelMeeting = async contactId => {
    isScheduling.value = true;
    try {
      const response = await ContactMeetingsAPI.cancel(contactId);
      applyContact(response.data.payload);
      useAlert(t('FOLLOW_UP.MEETING.CANCEL_SUCCESS'));
      return true;
    } catch (error) {
      useAlert(
        error?.response?.data?.error || t('FOLLOW_UP.MEETING.CANCEL_ERROR')
      );
      return false;
    } finally {
      isScheduling.value = false;
    }
  };

  return {
    isScheduling,
    schedulingProviders,
    isProviderConnected,
    providerAction,
    scheduleMeeting,
    rescheduleMeeting,
    cancelMeeting,
  };
}
